import PubSub from 'pubsub-js';
import { useDispatch, useSelector } from "react-redux";
import {
  closeLightColorPopup,
  curatorLightSelector,
  openLightColorPopup,
  selectCameraDataBeforeSelectingLight,
  setCameraDataBeforeSelectingLight,
  setSelectedLightId,
} from "../../../../../redux/slicers/admin/curatorLightSlicer";
import {
  syncLightFromUnity,
  updateLight,
  curatorActions,
  selectLightList,
} from "../../../../../redux/slicers/admin/curatorSlicer";
import { useInfoDialogaActions } from "../../../../../hooks/useInfoDialogaActions";
import { useUnityContext } from "../../../../container/unityContainer";
import { toUnityFixed } from "../../../../../helpers/unityHelper";
import { undoRedo } from "../../UndoRedo/UndoRedo";
import { updateByPath } from "../../../../../helpers/jsHelper";
import { getAppState } from "../../../../../redux/store";
import { errorToast } from "../../../../../helpers/toastHelper";
import { UnityEvent, waitForEventOnce } from "../../../../../helpers/unityPubSub";
import { setCuratorNotSaved } from '../../../../../pages/admin/curator/curatorState';
import { convertActiveUnitToMeters } from '../../../../../helpers/curatorActions';

export const useUnityLight = () => {
  const dispatch = useDispatch();
  const unityContext = useUnityContext();
  const { openInfoDialog } = useInfoDialogaActions();
  const selectedLightId = useSelector(
    (state) => curatorLightSelector(state).selectedLightId
  );

  const selectLight = async (light, syncLightData) => {
    const cameraDataBeforeSelectingLight = selectCameraDataBeforeSelectingLight(getAppState());

    if (!cameraDataBeforeSelectingLight) {
      const { cameraData } = await waitForEventOnce(UnityEvent.CameraDetailsReceiver, unityContext.handleCreateCamera)
      dispatch(setCameraDataBeforeSelectingLight(cameraData));
      
    }

    unityContext.lightModule.SelectLightObjectReceiver(light.id, syncLightData);
  };

  const selectCheckedLight = async (light, syncLightData, addObject) => {
    unityContext.lightModule.SelectLightUsingCheckBoxReceiver(light.id, syncLightData , addObject);
  };

  const toggleLightEnabled = (light) => {
    const newActive = !light.enableInRendering
    setLightEnabled(light, newActive);

    undoRedo.light.toggleLight({ id: light.id, enabled: newActive })
  };

  const setLightEnabled = (light, active) => {
    if (!active) {
      dispatch(setSelectedLightId(null))
    }

    unityContext.lightModule.UpdateEnableInRenderingReceiver(
      light.id,
      active
    );
    dispatch(
      updateLight({
        ...light,
        enableInRendering: active,
      })
    );

    setCuratorNotSaved();
  }


  const openColorPopup = (light, type, color) => {
    dispatch(openLightColorPopup({ light, type, color }));
  };

  const closeColorPopup = (light, type, color) => {
    dispatch(closeLightColorPopup());

    if (light) {
      // reset light while closing popup
      updateLightColor(light, type, light.lightDetails[type].lightColor);
    }
  };

  const updateLightColor = (light, type, color) => {
    const updatedLight = {
      ...light,
      lightDetails: {
        ...light.lightDetails,
        [type]: {
          ...light.lightDetails[type],
          lightColor: color,
        },
      },
    };
    unityContext.lightModule.UpdateColorReceiver(color);
    dispatch(updateLight(updatedLight));

    setCuratorNotSaved();
  };

  const modifyLight = (light, name, value) => {
    // TODO: handle number values here
    const updatedLight = updateByPath(light, name, value);
    let valueInMeters;
    
    switch (name) {
      case "lightDetails.Area.lightColor":
      case "lightDetails.Direction.lightColor":
      case "lightDetails.Spot.lightColor":
      case "lightDetails.Point.lightColor":
        unityContext.lightModule.UpdateColorReceiver(value);
        break;
      case "lightDetails.Area.intensity":
      case "lightDetails.Direction.intensity":
      case "lightDetails.Spot.intensity":
      case "lightDetails.Point.intensity":
        unityContext.lightModule.UpdateIntensityReceiver(parseFloat(value));
        break;
      case "lightType":
        unityContext.lightModule.UpdateLightTypeReceiver(updatedLight);
        break;
      case "lightDetails.Point.size": {
        if (typeof value === 'string' && value.trim() === '') break;
        valueInMeters = convertActiveUnitToMeters(toUnityFixed(value));
        updatedLight.lightDetails.Point.size = valueInMeters
        unityContext.lightModule.UpdateSizeReceiver(valueInMeters);
        break;
      }
      case "lightDetails.Area.size":
      case "lightDetails.Direction.size":
      case "lightDetails.Spot.size":
        unityContext.lightModule.UpdateSizeReceiver(parseFloat(value));
        break;
      case "visibleInReflection":
        unityContext.lightModule.UpdateVisibleInReflectionReceiver(value);
        break;
      case "visibleInGlass":
        unityContext.lightModule.UpdateVisibleInGlassReceiver(value);
        break;
      case "visibleInGlossyMaterials":
        unityContext.lightModule.UpdateVisibleInGlossyMaterialsReceiver(value);
        break;
      case "spotAngle":
        unityContext.lightModule.UpdateSpotAngleReceiver(parseFloat(value));
        break;
      case "blendSpot":
        unityContext.lightModule.UpdateBlendSpotReceiver(parseFloat(value));
        break;
      case "lightDetails.Spot.blurShadow":
        unityContext.lightModule.UpdateSizeReceiver(parseFloat(value));
        break;
      case "sizeWidth":
        if (typeof value === 'string' && value.trim() === '') break;
        valueInMeters = convertActiveUnitToMeters(toUnityFixed(value));
        updatedLight.sizeWidth = valueInMeters;
        unityContext.lightModule.UpdateAreaWidthReceiver(updatedLight.sizeWidth);
        break;
      case "sizeHeight":
        if (typeof value === 'string' && value.trim() === '') break;
        valueInMeters = convertActiveUnitToMeters(toUnityFixed(value));
        updatedLight.sizeHeight = valueInMeters;
        unityContext.lightModule.UpdateAreaHeightReceiver(updatedLight.sizeHeight);
        break;
      default:
        throw new Error("Missing handler for " + name);
    }

    dispatch(updateLight(updatedLight));
    setCuratorNotSaved();

  };


  const createChangeHandler = (light) => (event) => {
    let { name, value, type, checked } = event.target;
    value = type === 'checkbox' ? checked : value;
    modifyLight(light, name, value)
  }

  const addLight = (lightName, { skipHistory = false, lightId } = {}) => {
    const newLightName = lightName.trim();
    const lightList = selectLightList(getAppState());
    const nameExist = lightList.find(light => light.lightName?.toLowerCase() === newLightName.toLowerCase());
    if (nameExist) {
      errorToast('Light with this name already exist')
      return;
    }

    if (!skipHistory) {
      PubSub.subscribeOnce(UnityEvent.SelectedLightReceiver, (_, light) => {
        undoRedo.light.addLight({ name: lightName, lightId: light.id })
      })
    }

    if (lightId) {
      unityContext.lightModule.GenerateLightByNameReceiver({ name: lightName, id: lightId });
    } else {
      unityContext.lightModule.GenerateLightReceiver(lightName);
    }
    setCuratorNotSaved();
  };

  const toggleEdit = (light) => {
    if (selectedLightId === light.id) {
      dispatch(setSelectedLightId(null));
    } else {
      dispatch(setSelectedLightId(light.id));
      selectLight(light, true);
    }
  };

  const toggleLightDetails = (id) => {
    dispatch(setSelectedLightId(id ? id : null));
  };

  const focusLightObject = (light) => {
    if (selectedLightId === light.id) {
      // no need to focus light as it's already focused
      return;
    }
    if (window.$stopFocusEvent) {
      window.$stopFocusEvent = null
      return
    }

    // dispatch(setSelectedLightId(null));
    selectLight(light, false)
    setCuratorNotSaved();
  }

  const forceDeleteLight = (id) => {
    unityContext.lightModule.OnDeleteLightFromUI(id);
    dispatch(curatorActions.deleteLight(id));
    setCuratorNotSaved();
  }

  const deleteLight = (light) => {

    openInfoDialog({
      title: "Delete",
      description: `Do you want to delete ${light.lightName}`,
      cancelButtonText: "No",
      buttonText: "Yes",
      onButtonClick: () => {
        forceDeleteLight(light.id)
      },
    });
  };

  return {
    addLight,
    selectLight,
    toggleEdit,
    createChangeHandler,
    deleteLight,
    toggleLightEnabled,
    openColorPopup,
    closeColorPopup,
    updateLightColor,
    setLightEnabled,
    modifyLight,
    focusLightObject,
    forceDeleteLight,
    toggleLightDetails,
    selectCheckedLight,
  };
};
